<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card overflow-hidden border-0 shadow-sm">
          <div class="card-header p-4 bg-gradient-primary text-white">
            <h5 class="mb-1 font-weight-bold">Importación de Registros de Cargas Másivas</h5>
            <p class="text-sm mb-0 opacity-8">Sube un archivo Excel (.xls, .xlsx) para cargar datos de registros al sistema</p>
          </div>
          
          <!-- Sección de carga de archivo -->
          <div class="card-body p-4" v-if="!isPreviewMode && !showConsole">
            <div class="row">
              <div class="col-12">
                <transition name="fade" mode="out-in">
                  <FileUploadZone 
                    :fileType="fileType"
                    :file="file"
                    :isDragging="isDragging"
                    :thumbnail="fileThumbnail"
                    @file-selected="handleFileSelected"
                    @file-drop="handleFileDrop"
                    @dragging-change="updateDragging"
                    @reset="resetFile"
                    @show-preview="processExcelFile"
                  />
                </transition>
              </div>
            </div>
            
            <!-- Mensaje cuando no hay archivos seleccionados -->
            <div v-if="!file && !processingFile" class="text-center p-5 text-secondary empty-state mt-4">
              <i class="ri-upload-cloud-line fs-1 mb-3"></i>
              <h5>Ningún archivo seleccionado</h5>
              <p class="text-sm text-muted">Selecciona un archivo Excel con datos de registros para comenzar</p>
            </div>
          </div>
          
          <!-- Indicador de carga -->
          <LoadingIndicator v-if="isLoading" />
          
          <!-- Mostrar valor de celda A1 de manera destacada -->
        <div v-if="isPreviewMode && !isLoading && (showCellA1 || showCellE2)" class="px-4 py-3 bg-light border-bottom">
          <div class="d-flex flex-wrap align-items-center">
            <!-- Mostrar celda A1 (Código de archivo) -->
            <div v-if="showCellA1" class="cell-a1-container me-3 mb-2">
              <span class="cell-a1-label font-weight-bold">{{ cellA1Title }}</span>
              <span class="cell-a1-value badge bg-primary text-white font-size-lg ms-2">{{ cellA1Value }}</span>
            </div>
            
            <!-- Mostrar máximo código generado (basado en E2) -->
            <div v-if="showCellE2" class="cell-a1-container mb-2">
              <span class="cell-a1-label font-weight-bold">{{ maxCodObraTitle }}</span>
              <span v-if="isLoadingMaxCodObra" class="cell-a1-value badge bg-secondary text-white font-size-lg ms-2">
                <i class="ri-loader-2-line ri-spin me-1"></i> Cargando...
              </span>
              <span v-else class="cell-a1-value badge bg-info text-white font-size-lg ms-2">{{ maxCodObraValue || 'No encontrado' }}</span>
            </div>
          </div>
        </div>
          
          <!-- Vista previa del archivo con paginación -->
          <DataPreview 
            v-if="isPreviewMode && !isLoading && !showConsole"
            :currentPreviewType="fileType"
            :fileName="file?.name || ''"            
            :sheetNames="sheetNames"
            :selectedSheet="selectedSheet"
            :headers="headers"
            :allData="allData"
            :totalRows="totalRows"
            :currentPage="currentPage"
            :pageSize="pageSize"
            :codesMatch="codesMatch"
            :validationMessage="validationMessage"
            :showValidation="true"
            @sheet-change="changeSheet"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
            @exit-preview="exitPreview"
            @import-data="startImportProcess"
          />
          
          <!-- Consola de ejecución estilo hacker -->
          <div v-if="showConsole" class="bg-dark">
            <ConsoleLog 
              ref="hackerConsole"
              title="Terminal de procesamiento"
              consoleTitle="transformer@teleconnect:~/data-export"
              prompt="transformer@teleconnect:~$"
              :isComplete="consoleComplete"
              completionMessage="Proceso completado exitosamente"
              @close="resetConsole"
              @complete="updateConsoleComplete"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal de confirmación -->
  <ImportConfirmationModal
    v-if="showConfirmModal"
    v-model:visible="confirmModalVisible"
    :filesToImport="[fileType]"
    :files="{users: file}"
    :previewDataMap="previewDataMap"
    :importing="importing"
    @confirm="importData"
    @cancel="confirmModalVisible = false"
  />
</template>

<script>
import { defineComponent, ref, reactive, defineAsyncComponent, shallowRef, watch, nextTick, onUnmounted, computed, onMounted } from 'vue';
import showSwal from "/src/mixins/showSwal.js";
import * as XLSX from 'xlsx';
import axios from 'axios';

// Constantes - extraídas del componente para mejorar la legibilidad
import { 
  FILE_TYPE, 
  ALLOWED_EXCEL_TYPES, 
  EXCEL_READ_OPTIONS, 
  THUMBNAIL_SAMPLE_SIZE, 
  MODAL_CLOSE_TIMEOUT,
  PROCESS_CHUNK_SIZE
} from '@/views/users/constants';

// Utilidades - extraídas a un archivo separado
import { formatFileSize, generateUniqueId } from '@/utils/fileUtils';

// Importamos componentes con Lazy Loading
const FileUploadZone = defineAsyncComponent(() => import('./components/FileUploadZone.vue'));
const LoadingIndicator = defineAsyncComponent(() => import('./components/LoadingIndicator.vue'));
const DataPreview = defineAsyncComponent({
  loader: () => import('./components/DataPreview.vue'),
  loadingComponent: LoadingIndicator,
  delay: 200
});
const ImportConfirmationModal = defineAsyncComponent(() => import('./components/ImportConfirmationModal.vue'));
const ConsoleLog = defineAsyncComponent(() => import('./components/ConsoleLog.vue'));

// Composables - módulos reutilizables
import useNotification from '@/composables/useNotification';
import useFileProcessing from '@/composables/useFileProcessing';

export default defineComponent({
  name: "CargaMasiva",
  components: {
    FileUploadZone,
    LoadingIndicator,
    DataPreview,
    ImportConfirmationModal,
    ConsoleLog
  },
  setup() {
    // Composables
    const { showNotification } = useNotification(showSwal);
    
    // Variables reactivas
    const file = ref(null);
    const fileThumbnail = ref(null);
    const processingId = ref(generateUniqueId()); // Identificador único para cada procesamiento
    
    // Estados UI
    const isLoading = ref(false);
    const isDragging = ref(false);
    const isPreviewMode = ref(false);
    const importing = ref(false);
    const confirmModalVisible = ref(false);
    const showConfirmModal = ref(false);
    
    // Estados de procesamiento
    const processingFile = ref(false);
    const processingProgress = ref(0);
    
    // Almacenamiento de datos
    const previewDataMap = reactive({ users: [] });
    const headersMap = reactive({ users: [] });
    
    // Datos para vista previa
    const headers = ref([]);
    const allData = shallowRef([]);
    const totalRows = ref(0);
    
    // Nuevo: Variable para almacenar el valor de la celda A1
    const cellA1Value = ref('');
    const cellA1Title = ref('Código de archivo:');
    const showCellA1 = ref(false);

    // Nuevas variables para E2
    const cellE2Value = ref('');
    const cellE2Title = ref('Código de proyecto:');
    const showCellE2 = ref(false);    
    
    // Variables para el máximo código de obra (basado en E2)
    const maxCodObraValue = ref('');
    const maxCodObraTitle = ref('Último CodObra:');
    const isLoadingMaxCodObra = ref(false);
    
    // Paginación
    const currentPage = ref(1);
    const pageSize = ref(50);
    
    // Nueva variable reactiva para almacenar el mapeo de columnas
    const columnMapping = ref({});

    // Datos calculados para paginación
    const paginatedData = computed(() => {
      const startIndex = (currentPage.value - 1) * pageSize.value;
      return allData.value.slice(startIndex, startIndex + pageSize.value);
    });
    
    // Manejo de hojas Excel
    const workbook = shallowRef(null);
    const sheetNames = ref([]);
    const selectedSheet = ref("");
    
    const workbookMap = reactive({ users: null });
    const sheetNamesMap = reactive({ users: [] });
    const selectedSheetMap = reactive({ users: "" });
    
    // Variables para la consola de ejecución
    const showConsole = ref(false);
    const consoleComplete = ref(false);
    const hackerConsole = ref(null);
    
    // Función para formatear fechas
    const formatDate = (value) => {
      if (!value) return '';
      
      // Verificar si es un objeto Date o una cadena que representa una fecha
      let date;
      if (value instanceof Date) {
        date = value;
      } else if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}|GMT/.test(value)) {
        date = new Date(value);
      } else {
        return value; // No es una fecha, devolver el valor original
      }
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        return value; // Fecha inválida, devolver el valor original
      }
      
      // Formatear como YYYY-MM-DD
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    };
    
    // Lista de columnas que contienen fechas
    const dateColumns = [
      'F_COMM_TOTAL', 'F_Desde', 'W_As_fecGest', 'F_OBJETIVO', 
      'PLA', 'E04-E-Creacion', 'F_DATOS', 'E69-CO'
    ];

    // Función para obtener el mapeo de columnas desde el backend
    const fetchColumnMapping = async () => {
      try {
        const response = await axios.get('/api/v2/cargamasiva/mapeo-columnas', {
          params: { tipo_csv: 'comm_con_01' }
        });
        
        if (response.data.success && response.data.mapeo) {
          columnMapping.value = response.data.mapeo;
          console.log('Mapeo de columnas obtenido:', columnMapping.value);
        } else {
          console.error('Error al obtener mapeo de columnas:', response.data.message || 'Respuesta incorrecta');
          // Usar mapeo predeterminado en caso de error
          columnMapping.value = {
            'Empl': 'site_cod',
            'C_OBRA': 'cod_externo_cliente',
            'ATLAS_JALONES': 'tx_estado_cliente',
            'E69-CO': 'seg_fin_total_fecha'
          };
        }
      } catch (error) {
        console.error('Error al obtener mapeo de columnas:', error);
        // Usar mapeo predeterminado en caso de error
        columnMapping.value = {
          'Empl': 'site_cod',
          'C_OBRA': 'cod_externo_cliente',
          'ATLAS_JALONES': 'tx_estado_cliente',
          'E69-CO': 'seg_fin_total_fecha'
        };
      }
    };
    
    const extractPatternFromCodObra = (codValue) => {
      if (!codValue || typeof codValue !== 'string') {
        console.log('Valor inválido para extracción de patrón:', codValue);
        return null;
      }
      
      const strValue = String(codValue).trim();
      
      if (strValue === '') {
        console.log('Valor vacío después de trim');
        return null;
      }
      
      console.log('Extrayendo patrón de:', strValue, 'Tipo:', typeof strValue);
      
      // Encontrar la posición del primer y último guion
      const firstHyphenIndex = strValue.indexOf('-');
      const lastHyphenIndex = strValue.lastIndexOf('-');
      
      if (firstHyphenIndex === -1 || lastHyphenIndex === -1 || firstHyphenIndex >= lastHyphenIndex) {
        console.log('No se encontraron al menos dos guiones en el valor');
        return null;
      }
      
      // Extraer desde después del primer guion hasta antes del último guion
      const pattern = strValue.substring(firstHyphenIndex, lastHyphenIndex + 1);
      console.log('Patrón extraído:', pattern);
      
      // Añadir wildcards % al inicio y al final
      const finalPattern = '%' + pattern + '%';
      console.log('Patrón final con wildcards:', finalPattern);
      
      return finalPattern;
    };

// Función para obtener el máximo código de obra desde la base de datos
    const fetchMaxCodObra = async (pattern) => {
      console.log('fetchMaxCodObra llamado con patrón:', pattern);
      
      if (!pattern) {
        console.log('No se recibió un patrón válido');
        maxCodObraValue.value = 'N/A';
        return;
      }
      
      isLoadingMaxCodObra.value = true;
      
      try {
        // Construir la URL completa para debug
        const baseURL = axios.defaults.baseURL || window.location.origin;
        const fullURL = `${baseURL}/api/v2/cargamasiva/max-cod-obra`;
        console.log('URL completa de la petición:', fullURL);
        console.log('Enviando petición con datos:', { value: pattern });
        
        const response = await axios.post('/api/v2/cargamasiva/max-cod-obra', {
          value: pattern
        }, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        console.log('Respuesta exitosa:', response);
        
        if (response.data) {
          let maxValue = null;
          
          if (response.data.maxNumCodObra !== undefined) {
            maxValue = response.data.maxNumCodObra;
          } else if (response.data.maxnumcodobra !== undefined) {
            maxValue = response.data.maxnumcodobra;
          }
          
          if (maxValue !== null && maxValue !== undefined) {
            console.log('Valor máximo obtenido:', maxValue);
            maxCodObraValue.value = maxValue.toString();
          } else {
            console.log('El valor máximo es null o no existe');
            maxCodObraValue.value = '0 (nuevo patrón)';
          }
        } else {
          console.log('Respuesta vacía del servidor');
          maxCodObraValue.value = 'No encontrado';
        }
      } catch (error) {
        console.error('Error completo:', error);
        console.error('Error config:', error.config);
        console.error('Error request:', error.request);
        console.error('Error response:', error.response);
        
        if (error.response) {
          console.error('Error status:', error.response.status);
          console.error('Error data:', error.response.data);
          console.error('Error headers:', error.response.headers);
          
          // Verificar si es un problema de CORS
          if (error.response.status === 0) {
            console.error('Error CORS detectado');
            maxCodObraValue.value = 'Error CORS';
          } else if (error.response.status === 404) {
            console.error('Ruta no encontrada (404)');
            maxCodObraValue.value = 'Ruta no encontrada';
          } else {
            maxCodObraValue.value = `Error ${error.response.status}`;
          }
        } else if (error.request) {
          console.error('Error: No se recibió respuesta del servidor');
          maxCodObraValue.value = 'Sin respuesta del servidor';
        } else {
          console.error('Error al configurar la petición:', error.message);
          maxCodObraValue.value = 'Error de configuración';
        }
      } finally {
        isLoadingMaxCodObra.value = false;
      }
    };

    const codesMatch = computed(() => {
      if (!cellA1Value.value || !maxCodObraValue.value) {
        return false;
      }
      
      // Convertir ambos valores a string y compararlos
      const codeA1 = String(cellA1Value.value).trim();
      const maxCode = String(maxCodObraValue.value).trim();
      
      // Log para debugging (opcional)
      console.log('Comparando códigos:', { codeA1, maxCode, match: codeA1 === maxCode });
      
      return codeA1 === maxCode;
    });
    
    const validationMessage = computed(() => {
      if (!cellA1Value.value || !maxCodObraValue.value) {
        return '';
      }
      
      if (!codesMatch.value) {
        return `El código de archivo (${cellA1Value.value}) no coincide con el último CodObra (${maxCodObraValue.value})`;
      }
      
      return '';
    });    
    
    // Inicializar el composable de procesamiento de archivos
    const { 
      generateThumbnail, 
      validateExcelFile,
      processExcelFileData,
      processDataInChunks
    } = useFileProcessing({ 
      showNotification,
      EXCEL_READ_OPTIONS,
      THUMBNAIL_SAMPLE_SIZE
    });
    
    // Observar cambios en confirmModalVisible para cargar/descargar el componente
    watch(confirmModalVisible, (newVal) => {
      if (newVal) {
        showConfirmModal.value = true;
      } else {
        // Dar tiempo para que se complete la animación de cierre
        setTimeout(() => {
          showConfirmModal.value = false;
        }, MODAL_CLOSE_TIMEOUT);
      }
    });
    
    // Al montar el componente
    onMounted(() => {
      fetchColumnMapping();
    });

    // Al destruir el componente, liberamos memoria
    onUnmounted(() => {
      cleanupResources();
    });
    
    // Limpieza de recursos para evitar fugas de memoria
    const cleanupResources = () => {
      workbook.value = null;
      allData.value = [];
      workbookMap.users = null;
      processingId.value = generateUniqueId(); // Regeneramos ID para evitar procesos pendientes
    };
    
    // Event handlers
    const updateDragging = (value) => {
      isDragging.value = value;
    };
    
    const updateConsoleComplete = (value) => {
      consoleComplete.value = value;
    };
    
    const handlePageChange = (page) => {
      currentPage.value = page;
      // Registramos interacción para analíticas (opcional)
      logUserInteraction('pagination_change', { page });
    };
    
    const handlePageSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1; // Reset a la primera página cuando cambia el tamaño
      // Registramos interacción para analíticas (opcional)
      logUserInteraction('page_size_change', { size });
    };
    
    const startImportProcess = () => {
      confirmModalVisible.value = true;
    };
    
    // Analíticas - registro de interacciones del usuario (opcional)
    const logUserInteraction = (action, data = {}) => {
      // Implementación futura - podría enviar datos al backend o a una herramienta de analítica
      console.debug('User interaction:', action, data);
    };
    
    // File handling methods
    const handleFileSelected = (selectedFile) => {
      if (!validateExcelFile(selectedFile, ALLOWED_EXCEL_TYPES)) return;
      
      file.value = selectedFile;
      generateThumbnail(selectedFile, (thumbnailData) => {
        fileThumbnail.value = thumbnailData;
      });
      
      // Registramos interacción para analíticas (opcional)
      logUserInteraction('file_selected', { 
        fileSize: selectedFile.size,
        fileType: selectedFile.type 
      });
    };
    
    const handleFileDrop = (selectedFile) => {
      if (!validateExcelFile(selectedFile, ALLOWED_EXCEL_TYPES)) return;
      
      file.value = selectedFile;
      generateThumbnail(selectedFile, (thumbnailData) => {
        fileThumbnail.value = thumbnailData;
      });
      
      // Registramos interacción para analíticas (opcional)
      logUserInteraction('file_dropped', { 
        fileSize: selectedFile.size,
        fileType: selectedFile.type 
      });
    };
    
    const resetFile = () => {
      file.value = null;
      fileThumbnail.value = null;
      previewDataMap.users = [];
      headersMap.users = [];
      workbookMap.users = null;
      sheetNamesMap.users = [];
      selectedSheetMap.users = "";
      isPreviewMode.value = false;
      processingId.value = generateUniqueId(); // Nuevo ID para cancelar operaciones pendientes
      
      // Resetear el valor de A1 y máximo código de obra
      cellA1Value.value = '';
      showCellA1.value = false;
      maxCodObraValue.value = '';
    };
    
    // Excel processing methods
    const processExcelFile = async () => {
      if (!file.value) return;
      
      const currentProcessingId = generateUniqueId();
      processingId.value = currentProcessingId;
      
      isLoading.value = true;
      processingFile.value = true;
      processingProgress.value = 10; // Iniciar progreso
      isPreviewMode.value = true;
      
      try {
        // Procesamos el archivo Excel usando la utilidad extraída
        const result = await processExcelFileData(file.value, (progress) => {
          // Solo actualizamos el progreso si este procesamiento sigue siendo el actual
          if (processingId.value === currentProcessingId) {
            processingProgress.value = progress;
          }
        });
        
        // Verificamos si este procesamiento fue cancelado
        if (processingId.value !== currentProcessingId) {
          return; // Salimos temprano si se canceló
        }
        
        // Actualizar datos globales
        workbookMap.users = result.workbook;
        sheetNamesMap.users = result.sheets;
        
        workbook.value = result.workbook;
        sheetNames.value = result.sheets;
        
        // Seleccionar primera hoja por defecto
        if (result.sheets.length > 0) {
          const firstSheet = result.sheets[0];
          selectedSheetMap.users = firstSheet;
          selectedSheet.value = firstSheet;
          
          // Debemos esperar a que Vue actualice el DOM antes de continuar
          await nextTick();
          await previewSelectedSheet(currentProcessingId);
        } else {
          showNotification('error', 'Archivo sin hojas', 'El archivo Excel no contiene hojas de cálculo.');
          resetProcessingState();
        }
      } catch (error) {
        console.error('Error al procesar el archivo:', error);
        showNotification('error', 'Error de procesamiento', 
          'Error al procesar el archivo Excel. Verifica que el formato sea correcto.');
        resetProcessingState();
      }
    };
    
    const resetProcessingState = () => {
      isLoading.value = false;
      processingFile.value = false;
      processingProgress.value = 0;
      isPreviewMode.value = false;
    };
    
    const previewSelectedSheet = async (currentProcessingId) => {
      if (!selectedSheet.value || !workbookMap.users) return;
      
      isLoading.value = true;
      headers.value = [];
      allData.value = [];
      currentPage.value = 1;
      
      try {
        // Obtener la hoja seleccionada
        const worksheet = workbookMap.users.Sheets[selectedSheet.value];
        
        // SOLO procesar A1 y E2 si sus valores están vacíos (primera vez)
        if (!cellA1Value.value && worksheet && worksheet['A1']) {
          cellA1Value.value = worksheet['A1'].v || '';
          showCellA1.value = !!cellA1Value.value;
        }
        
        if (!cellE2Value.value && worksheet && worksheet['E2']) {
          cellE2Value.value = worksheet['E2'].v || '';
          showCellE2.value = !!cellE2Value.value;
          
          console.log('Valor extraído de E2:', cellE2Value.value, 'Tipo:', typeof cellE2Value.value);
          
          if (cellE2Value.value) {
            const pattern = extractPatternFromCodObra(cellE2Value.value);
            if (pattern) {
              await fetchMaxCodObra(pattern);
            }
          }
        }
        
        // Resto del código para procesar la hoja...
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Verificamos si este procesamiento fue cancelado
        if (processingId.value !== currentProcessingId) {
          return;
        }
        
        // Verificar datos mínimos
        if (jsonData.length <= 1) {
          showNotification('error', 'Hoja vacía', 'La hoja seleccionada no contiene datos suficientes.');
          isLoading.value = false;
          return;
        }
        
        // Tomar la primera fila como encabezados
        const headerRow = jsonData[0];
        
        // Validar estructura de encabezados
        if (!validateHeaders(headerRow)) {
          showNotification('warning', 'Encabezados incompletos', 
            'Algunos encabezados parecen estar vacíos o mal formateados. Revisa tu archivo Excel.');
        }
        
        headers.value = headerRow;
        headersMap.users = headerRow;
        
        // Procesar datos en chunks para mejor rendimiento
        const finalData = await processDataInChunks(jsonData, PROCESS_CHUNK_SIZE, (_, total) => {
          if (processingId.value !== currentProcessingId) {
            return false;
          }
          
          // Establecer el total correcto una sola vez
          totalRows.value = total;
          
          return true;
        });
        
        // Formatear fechas en los datos finales
        const formattedData = finalData.map(row => {
          return row.map((cell, cellIndex) => {
            const columnName = cellIndex < headerRow.length ? headerRow[cellIndex] : null;
            
            if (columnName && dateColumns.includes(columnName)) {
              return formatDate(cell);
            }
            
            return cell;
          });
        });
        
        // Establecer los datos finales formateados
        allData.value = formattedData;
        previewDataMap.users = formattedData;
        
        if (processingId.value === currentProcessingId) {
          setTimeout(() => {
            isLoading.value = false;
            processingFile.value = false;
            processingProgress.value = 100;
            
            setTimeout(() => {
              processingProgress.value = 0;
            }, 1000);
            
            showNotification('success', 'Hoja cargada', 
              `Se ha cargado con éxito la hoja "${selectedSheet.value}" con ${totalRows.value} registros.`);
              
            logUserInteraction('sheet_loaded', { 
              sheetName: selectedSheet.value,
              rowCount: totalRows.value 
            });
          }, 300);
        }
      } catch (error) {
        console.error('Error al procesar la hoja:', error);
        showNotification('error', 'Error de procesamiento', 
          'Error al procesar la hoja seleccionada. Verifica que el formato sea correcto.');
        isLoading.value = false;
        processingFile.value = false;
      }
    };
    
    // Validación de encabezados
    const validateHeaders = (headers) => {
      if (!headers || !Array.isArray(headers)) return false;
      
      // Verificar que al menos tengamos encabezados válidos
      const validHeadersCount = headers.filter(header => 
        header !== undefined && header !== null && header !== '').length;
      
      return validHeadersCount > 0;
    };
    
    const changeSheet = (sheetName) => {
      selectedSheet.value = sheetName;
      selectedSheetMap.users = sheetName;
      const currentProcessingId = processingId.value;
      
      previewSelectedSheet(currentProcessingId);
      
      // Registramos interacción para analíticas (opcional)
      logUserInteraction('sheet_changed', { sheetName });
    };
    
    const exitPreview = () => {
      isPreviewMode.value = false;
      processingFile.value = false;
      processingProgress.value = 0;
      allData.value = [];
      
      // Cancelamos cualquier procesamiento pendiente
      processingId.value = generateUniqueId();
    };
    
    // Data export and import
    const generateCSVContent = () => {
      let csvContent = "";
      
      // Columnas que queremos incluir en el CSV (basado en el mapeo dinámico)
      const requiredColumns = Object.keys(columnMapping.value);
      
      // Encontrar los índices de las columnas requeridas
      const columnIndices = [];
      const filteredHeaders = [];
      
      if (headers.value && headers.value.length > 0) {
        headers.value.forEach((header, index) => {
          if (requiredColumns.includes(header)) {
            columnIndices.push(index);
            filteredHeaders.push(header);
          }
        });
        
        // Agregar encabezados filtrados al CSV
        csvContent += filteredHeaders.join(";") + "\n";
      }
      
      // Agregar filas de datos filtradas - solo las columnas requeridas
      const rows = [];
      previewDataMap.users.forEach(row => {
        const filteredRow = columnIndices.map(index => {
          const cell = row[index];
          
          if (cell === null || cell === undefined) {
            return "";
          }
          
          let cellStr = String(cell);
          
          // Sólo escapamos si es necesario
          if (cellStr.includes(";") || cellStr.includes('"') || cellStr.includes("\n")) {
            cellStr = cellStr.replace(/"/g, '""');
            cellStr = `"${cellStr}"`;
          }
          
          return cellStr;
        });
        
        rows.push(filteredRow.join(";"));
      });
      
      csvContent += rows.join("\n");
      
      return "\ufeff" + csvContent; // BOM para UTF-8
    };
    
    const importData = async () => {
    confirmModalVisible.value = false;
    importing.value = true;
    
    try {
      // Generar CSV optimizado
      const csvContent = generateCSVContent();
      
      // Almacenamos el contenido en sessionStorage para uso futuro
      try {
        sessionStorage.setItem('last_exported_csv', csvContent.substring(0, 1000000)); // Limitamos tamaño
      } catch (e) {
        console.warn('No se pudo guardar en sessionStorage:', e);
      }
      
      // CAMBIO AQUÍ: Primero activamos la consola
      showConsole.value = true;
      isPreviewMode.value = false;
      
      // Esperamos a que el DOM se actualice y la consola se monte
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verificamos que hackerConsole esté disponible
      if (!hackerConsole.value) {
        console.error('La consola no está disponible');
        showNotification('error', 'Error de interfaz', 'No se pudo inicializar la consola de ejecución');
        importing.value = false;
        return;
      }
      
      // Mostramos el primer mensaje
      await hackerConsole.value.addLog("El proceso se ha iniciado", "info", 30);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mostramos el segundo mensaje antes de ejecutar el script
      await hackerConsole.value.addLog("Eliminando registros de la tabla auxiliar de Conectividad...", "info", 40);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // AHORA ejecutamos el script SQL para BORRAR
      try {
        const response = await axios.post('/api/v2/cargamasiva/borrar', {
          fileName: file.value ? file.value.name.replace(/\.[^/.]+$/, "") + ".csv" : "datos-exportados.csv",
          rowCount: totalRows.value,
          date: new Date().toISOString()
        });
        
        console.log('Respuesta del servidor:', response.data);
        
        if (response.data.success) {
          // Obtenemos la cantidad de registros eliminados
          let recordsAffected = 0;
          const serverResults = response.data.results;
          
          if (typeof serverResults === 'object' && serverResults.records_affected !== undefined) {
            recordsAffected = serverResults.records_affected;
          }
          
          // Mostrar mensaje de registros eliminados
          await hackerConsole.value.addLog(`Se han eliminado ${recordsAffected} registros de la tabla auxiliar de Conectividad.`, "success", 30);
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Nuevo mensaje: Iniciando carga de datos
          await hackerConsole.value.addLog("Iniciando carga de datos del archivo a la base de datos...", "info", 40);
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // AHORA cargar los datos - Enviamos el contenido del CSV en lugar del nombre del archivo
          try {
            const uploadId = generateUniqueId();
            
            // Enviamos el contenido del CSV directamente
            const uploadResponse = await axios.post('/api/v2/cargamasiva/cargar', {
              csvContent: csvContent,
              fileName: file.value ? file.value.name.replace(/\.[^/.]+$/, "") + ".csv" : "datos-exportados.csv",
              uploadId: uploadId,
              totalRows: totalRows.value,
              mappedColumns: Object.fromEntries(
                Object.values(columnMapping.value).map(field => [field, true])
              )
            });
            
            console.log('Respuesta de carga:', uploadResponse.data);
            
            if (uploadResponse.data.success) {
              const recordsInserted = uploadResponse.data.records_inserted || 0;
              
              // Mostramos el mensaje de registros insertados
              await hackerConsole.value.addLog(`Se han insertado ${recordsInserted} registros en la tabla aux_admin_actualiz_jalones_comm_con.`, "success", 30);
              await new Promise(resolve => setTimeout(resolve, 800));
              
              // Mostramos mensaje sobre la ejecución del segundo script
              await hackerConsole.value.addLog("Verificando cantidad de registros importados en la tabla auxiliar...", "info", 40);
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // Verificamos si tenemos resultados del segundo script
              if (uploadResponse.data.import_result && uploadResponse.data.import_result.success) {
                const totalCount = uploadResponse.data.import_result.total_count || 0;
                
                // Mostrar el conteo de registros del segundo script
                await hackerConsole.value.addLog(`Se han verificado ${totalCount} registros en la tabla auxiliar.`, "success", 30);
                await new Promise(resolve => setTimeout(resolve, 600));
                
                // Mostrar mensaje para el tercer script - actualización de jalones
                await hackerConsole.value.addLog("Actualizando registros en lista_tx según jalones importados...", "info", 40);
                await new Promise(resolve => setTimeout(resolve, 800));
                
                // Verificar si tenemos resultados del tercer script
                if (uploadResponse.data.update_tx_result && uploadResponse.data.update_tx_result.success) {
                  const updatedRecords = uploadResponse.data.update_tx_result.records_affected || 0;
                  
                  // Mostrar el número de registros actualizados por el tercer script
                  await hackerConsole.value.addLog(`Se han actualizado ${updatedRecords} registros de jalones en lista_tx.`, "success", 30);
                  await new Promise(resolve => setTimeout(resolve, 600));
                  
                  // Mensaje final de proceso completado
                  await hackerConsole.value.addLog("Proceso de importación completado exitosamente.", "success", 40);

                  // Marcamos la consola como completada
                  hackerConsole.value.setComplete(true);
                } else if (uploadResponse.data.update_tx_result) {
                  // Mostrar mensaje de error si hay un problema con el tercer script
                  await hackerConsole.value.addLog(`Error al actualizar jalones: ${uploadResponse.data.update_tx_result.message || 'Error desconocido'}`, "warning", 30);
                  await new Promise(resolve => setTimeout(resolve, 600));
                  
                  // Mensaje final indicando que se completó con advertencias
                  await hackerConsole.value.addLog("Proceso de importación completado con advertencias.", "warning", 40);
                  
                  // Marcamos la consola como completada
                  hackerConsole.value.setComplete(true);
                }
              } else {
                // Mensaje final en caso de error del segundo script
                await hackerConsole.value.addLog("Proceso de importación completado parcialmente.", "warning", 40);
                
                // Marcamos la consola como completada
                hackerConsole.value.setComplete(true);
              }
            } else {
              // Mostramos el error en la consola
              await hackerConsole.value.addLog(`ERROR: ${uploadResponse.data.message || 'Error al cargar los datos.'}`, "error", 30);
              showNotification('error', 'Error de carga', uploadResponse.data.message || 'Hubo un error al cargar los datos.');
              
              // Marcamos la consola como completada con error
              hackerConsole.value.setComplete(false);
            }
          } catch (uploadError) {
            console.error('Error al cargar datos:', uploadError);
            await hackerConsole.value.addLog(`ERROR: Error al cargar los datos: ${uploadError.message || 'Error de conexión'}`, "error", 30);
            showNotification('error', 'Error de carga', 'Hubo un problema al cargar los datos.');
            
            // Marcamos la consola como completada con error
            hackerConsole.value.setComplete(false);
          }
        } else {
          await hackerConsole.value.addLog("ERROR en script SQL: " + (response.data.message || 'Error al ejecutar el script de borrado.'), "error", 30);
          showNotification('error', 'Error de importación', response.data.message || 'Hubo un error al ejecutar el script SQL.');
          
          // Marcamos la consola como completada con error
          hackerConsole.value.setComplete(false);
        }
        
        importing.value = false;
      } catch (error) {
        console.error('Error:', error);
        importing.value = false;
        await hackerConsole.value.addLog(`ERROR: ${error.message || 'Error de conexión'}`, "error", 30);
        showNotification('error', 'Error de importación', error.message || 'Hubo un error al procesar la importación.');
        
        // Marcamos la consola como completada con error
        if (hackerConsole.value) {
          hackerConsole.value.setComplete(false);
        }
      }
    } catch (error) {
      console.error('Error al generar el CSV:', error);
      importing.value = false;
      showNotification('error', 'Error de exportación', 'Hubo un problema al generar el archivo CSV.');
      
      // Si la consola ya está visible, mostramos el error
      if (showConsole.value && hackerConsole.value) {
        hackerConsole.value.addLog(`ERROR: Error al generar el CSV: ${error.message || 'Error desconocido'}`, "error", 30);
        hackerConsole.value.setComplete(false);
      }
    }
  };

    // Reset console state
    const resetConsole = () => {
      showConsole.value = false;
      consoleComplete.value = false;
      importing.value = false; // Aseguramos que el estado de importación se resetee
      
      if (hackerConsole.value) {
        // Esperamos un tick para asegurar que la UI se actualice
        nextTick(() => {
          hackerConsole.value.clearLogs();
        });
      }
      
      // Pequeña pausa antes de resetear el archivo para mejorar UX
      setTimeout(() => {
        resetFile();
      }, 300);
    };

    return {
      // Constants
      fileType: FILE_TYPE,
      
      // State
      file,
      fileThumbnail,
      isLoading,
      isDragging,
      isPreviewMode,
      importing,
      confirmModalVisible,
      showConfirmModal,
      processingFile,
      processingProgress,
      previewDataMap,
      
      // Vista previa
      headers,
      allData,
      totalRows,
      currentPage,
      pageSize,
      workbook,
      sheetNames,
      selectedSheet,
      paginatedData,
      codesMatch,
      validationMessage,      
      
      // Valor de celda A1
      cellA1Value,
      cellA1Title,
      showCellA1,

  // Valores de celda E2 (Código de proyecto)
      cellE2Value,
      cellE2Title,
      showCellE2,      
      
      // Máximo código de obra
      maxCodObraValue,
      maxCodObraTitle,
      isLoadingMaxCodObra,
      
      // Consola
      showConsole,
      consoleComplete,
      hackerConsole,
      
      // Mapeo dinámico de columnas
      columnMapping,
      fetchColumnMapping,
      
      // Métodos
      handleFileSelected,
      handleFileDrop,
      resetFile,
      changeSheet,
      exitPreview,
      startImportProcess,
      handlePageChange,
      handlePageSizeChange,
      formatFileSize,
      importData,
      processExcelFile,
      resetConsole,
      updateDragging,
      updateConsoleComplete
    };
    }
    });
</script>

<style scoped>
/* Estilos base del componente principal */
.card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.card-header.bg-gradient-primary {
  border-radius: 12px 12px 0 0;
  background: linear-gradient(45deg, #4776E6 0%, #8E54E9 100%);
}

/* Estilos para el contenedor de la celda A1 */
.cell-a1-container {
  display: flex;
  align-items: center;
  padding: 8px 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  width: auto;
  display: inline-flex;
}

.cell-a1-label {
  color: #344767;
  font-size: 14px;
  margin-right: 10px;
  white-space: nowrap;
}

.cell-a1-value {
  font-size: 16px !important;
  font-weight: 600;
  padding: 6px 12px;
  background: linear-gradient(45deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border-radius: 6px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  min-width: 60px;
  text-align: center;
}

.cell-a1-value.badge.bg-info {
  background: linear-gradient(45deg, #22c55e 0%, #16a34a 100%) !important;
}

.cell-a1-value.badge.bg-secondary {
  background: linear-gradient(45deg, #6c757d 0%, #5a6268 100%) !important;
}

/* Estilos para los mensajes de validación */
.validation-message {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.validation-pending {
  background-color: #f8f9fa;
  color: #6c757d;
  border-left: 3px solid #6c757d;
}

.validation-success {
  background-color: #d1e7dd;
  color: #0f5132;
  border-left: 3px solid #0f5132;
}

.validation-warning {
  background-color: #fff3cd;
  color: #664d03;
  border-left: 3px solid #664d03;
}

.validation-error {
  background-color: #f8d7da;
  color: #842029;
  border-left: 3px solid #842029;
}

/* Animaciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.empty-state {
  padding: 3rem !important;
  color: #8898aa;
}

.empty-state i {
  opacity: 0.5;
}

/* Mejoramos el soporte para dispositivos móviles */
@media (max-width: 768px) {
  .empty-state {
    padding: 2rem !important;
  }
  
  .card-header.bg-gradient-primary {
    padding: 1rem !important;
  }
  
  .cell-a1-value {
    font-size: 14px !important;
    padding: 4px 8px;
  }
}
</style>